/**
 * OrderCompleteAlert - Alerta de orden completada
 *
 * Componente que muestra una notificación cuando una orden ha sido
 * completamente empacada. Incluye:
 * - Mensaje de confirmación de completado
 * - Botón para continuar con la siguiente orden
 * - Integración con el flujo de packing
 * - Animaciones y estilos de alerta
 */

import React, { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faPrint, faConveyorBelt } from '@fortawesome/pro-light-svg-icons';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { Shop } from '@shared/components/ui/shop';
import { useTranslation } from 'react-i18next';
import { AdjustPackagesModal } from '../modals';
import type { AdjustPackagesModalRef } from '../modals/AdjustPackagesModal';
import { usePrintOrder } from '../../hooks/usePrintOrder';

export interface OrderCompleteAlertProps {
  orderCode: string;
  orderName: string;
  orderId?: number;
  avatarSrc?: string;
  courierName?: string;
  courierImage?: string;
  trackingNumber?: string;
  onPrint?: () => void;
  onPacking?: () => void;
}

// 🎯 Componente que representa un pedido completado
export const OrderCompleteAlert = ({
  orderCode,
  orderName,
  orderId,
  avatarSrc,
  courierName,
  courierImage,
  trackingNumber,
  onPrint,
  onPacking,
}: OrderCompleteAlertProps) => {
  const { t } = useTranslation('packingModule');
  const { printOrder, isOrderLoading } = usePrintOrder();
  const adjustPackagesModalRef = useRef<AdjustPackagesModalRef>(null);

  const handleAdjustPackages = () => {
    adjustPackagesModalRef.current?.open();
  };

  const handleCancelPackages = () => {
    // TODO: Implementar cancelación
  };

  const handleConfirmPackages = (quantity: number) => {
    // TODO: Implementar confirmación de cantidad de bultos
  };

  // Función para manejar la impresión desde el alert
  const handlePrint = async () => {
    if (orderId) {
      await printOrder(orderId);
    } else if (onPrint) {
      // Fallback al onPrint prop si no hay orderId
      onPrint();
    }
  };

  return (
    <SectionBox className="mb-4 sm:!p-2">
      <div className="flex flex-col md:flex-row sm:items-center sm:justify-between gap-4">
        {/* Sección izquierda - Información principal del pedido */}
        <div className="flex flex-col gap-1">
          {/* Información de la orden */}
          <div className="flex items-center gap-3">
            <Shop src={avatarSrc} name={orderName} code={orderCode} className="!p-0" />
            {/* Estado de la orden */}
            <Badge
              variant="outline"
              className="bg-card-border light:bg-bg-success text-success light:text-bright-success border-0 px-3 py-1 text-xs"
            >
              <FontAwesomeIcon icon={faCheckCircle} className="w-3 h-3 mr-1" />
              {t('orders.status_complete')}
            </Badge>
          </div>

          {/* Información del courier - Imagen + nombre */}
          {courierName && (
            <div className="flex items-center gap-2 pl-11">
              {/* Courier - Imagen y nombre */}
              <Shop src={courierImage} name={courierName} code={trackingNumber} className="!p-0" />
            </div>
          )}
        </div>

        {/* Sección derecha - Botones de acción */}
        <div className="flex gap-2">
          {/* Botón de imprimir */}
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            disabled={orderId ? isOrderLoading(orderId) : false}
            title={t('actions.print_order')}
            data-testid="print-complete-alert-button"
          >
            <FontAwesomeIcon icon={faPrint} className="w-4 h-4 text-icons" />
          </Button>

          {/* Botón de bultos
          <Button
            variant="outline"
            size="sm"
            onClick={handleAdjustPackages}
            title={t('actions.adjust_packages')}
          >
            <FontAwesomeIcon
              icon={faConveyorBelt}
              className="w-4 h-4 text-icons"
            />
          </Button> */}
        </div>
      </div>

      {/* Modal de ajustar bultos */}
      <AdjustPackagesModal
        ref={adjustPackagesModalRef}
        initialQuantity={0}
        onCancel={handleCancelPackages}
        onConfirm={handleConfirmPackages}
      />
    </SectionBox>
  );
};
